// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import { type Task, type WorkCellTask, useEvolveGet, useEvolvePost } from '@evolvemep/api-ts';
import { Flex, Loader, Text } from '@mantine/core';

import { WrappedModal, useWrappedModal } from 'components/Mantine/Modals/WrappedModal';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type ReopenableWorkCellTask = Pick<
  WorkCellTask,
  'taskId' | 'taskName' | 'workOrderId' | 'workOrderIdentifier' | 'workOrderName'
>;

/** The two ways to point the modal at a task: pass the task, or the work cell task the caller already holds. */
export type ReopenTaskModalModeProps = RequireAtLeastOne<{
  /** The task to act on. Its work cell task is looked up when `workCellTask` is not supplied. */
  task?: Pick<Task, 'taskId'>;
  /** The task's work cell task, when the caller already has it. Supplying it skips the lookup `task` triggers. */
  workCellTask?: ReopenableWorkCellTask;
}>;

type Props = {
  opened: boolean;
  onClose: () => void;
  title: string;
  confirmationText: string;
  /**
   * Called once the task has been reopened. Receives the updated task, and the resolved work cell task so the
   * caller can name the task in its success notification.
   */
  onTaskReopened: (task: Task, workCellTask: ReopenableWorkCellTask) => Promise<void> | void;
  /** The verb phrase describing what happens to the task, e.g. `be set to In Progress`. */
  outcome: string;
  /** The gerund naming the action, used in the warning shown when the work order will reopen, e.g. `resuming`. */
  gerund: string;
} & ReopenTaskModalModeProps;

const ReopenTaskModalContents = ({
  workCellTask,
  outcome,
  gerund,
}: Pick<Props, 'outcome' | 'gerund'> & {
  workCellTask: ReopenableWorkCellTask;
}) => {
  const { setDisabled } = useWrappedModal();
  const { data: workOrder, loading } = useEvolveGet(`shop/workOrder/${workCellTask.workOrderId}`, {
    lazy: false,
  });

  useEffect(() => {
    setDisabled(loading);
  }, [setDisabled, loading]);

  if (loading) {
    return (
      <Flex justify="center">
        <Loader type="dots" />
      </Flex>
    );
  }

  return workOrder?.workOrderStateName === 'Completed' ? (
    <Text>
      This work order is currently closed. By {gerund} this task, <b>{getDisplayNameWO(workOrder)}</b> will reopen.{' '}
      <b>{workCellTask.taskName}</b> task will {outcome}. Do you want to continue?
    </Text>
  ) : (
    <Text>
      <b>{workCellTask.taskName}</b> for <b>{getDisplayNameWO(workCellTask)}</b> will {outcome}. Do you want to
      continue?
    </Text>
  );
};

/**
 * Confirms moving a completed task back out of the completed state, warning the user when doing so will also
 * reopen a closed work order.
 *
 * @remarks This is the shared body behind `ResumeTaskModal` and `UncompleteTaskModal` — use one of those rather than
 * rendering this directly, so the action keeps a name at the call site. Both post to `resume`, which returns the task
 * to Paused when it was started and to Not Started when it never was.
 */
export const ReopenTaskModal = ({
  task,
  workCellTask: parentWorkCellTask,
  outcome,
  gerund,
  onTaskReopened,
  ...props
}: Props) => {
  const { data: workCellTaskFromNetwork, apiCall: getWorkCellTask } = useEvolveGet(`shop/task/:taskId/workCellTask`);
  const workCellTask = parentWorkCellTask ?? workCellTaskFromNetwork;

  useEffect(() => {
    if (isNil(workCellTask) && isNotNil(task) && props.opened) {
      void getWorkCellTask({ url: `shop/task/${task.taskId}/workCellTask` });
    }
  }, [getWorkCellTask, props.opened, task, workCellTask]);

  const { apiCall: resumeTaskApiCall, loading: resuming } = useEvolvePost(`shop/task/:taskId/resume`);

  return (
    <WrappedModal
      {...props}
      onConfirm={async () => {
        if (isNotNil(workCellTask)) {
          const updatedTask = await resumeTaskApiCall({}, { url: `shop/task/${workCellTask.taskId}/resume` });
          await onTaskReopened(updatedTask, workCellTask);
        }
      }}
      loading={resuming}
      disabled={isNil(workCellTask)}
    >
      {isNil(workCellTask) ? (
        <Flex justify="center">
          <Loader type="dots" />
        </Flex>
      ) : (
        <ReopenTaskModalContents workCellTask={workCellTask} outcome={outcome} gerund={gerund} />
      )}
    </WrappedModal>
  );
};
